/**
 * SettingsDialog - Dialog pentru setări profil utilizator
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { User, Mail, Phone, Building2, Lock, Eye, EyeOff, Save, Download, Trash2, Shield, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsDialog({ open, onOpenChange, user, onUserUpdate }) {
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Profile form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // GDPR
  const [exporting, setExporting] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user && open) {
      setName(user.name || "");
      setEmail(user.email || "");
      setPhone(user.phone || "");
      setCompanyName(user.company_name || "");
    }
  }, [user, open]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(`${API}/settings/profile?user_id=${user.id}`, {
        name,
        email,
        phone,
        company_name: companyName
      });
      
      // Update local storage
      const updatedUser = { ...user, ...response.data.user };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }
      
      toast.success("Profil actualizat cu succes!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la salvare");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Parolele nu coincid!");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Parola trebuie să aibă minim 6 caractere");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(`${API}/settings/profile?user_id=${user.id}`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success("Parola a fost schimbată!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la schimbarea parolei");
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/gdpr/export-my-data?user_id=${user.id}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etesa_datele_mele_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Datele au fost exportate!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Eroare la exportul datelor");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error("Introduceți parola pentru confirmare");
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/gdpr/delete-my-account?user_id=${user.id}`, {
        data: { password: deletePassword }
      });
      toast.success("Contul a fost șters. Vei fi deconectat.");
      localStorage.removeItem("user");
      sessionStorage.removeItem("user");
      setTimeout(() => { window.location.href = "/login"; }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergere");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-violet-600" />
            Setări Cont
          </DialogTitle>
          <DialogDescription>
            Modifică datele contului tău
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="profile" className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">Profil</TabsTrigger>
            <TabsTrigger value="password">Parolă</TabsTrigger>
            <TabsTrigger value="gdpr" data-testid="settings-gdpr-tab">GDPR</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4 mt-4">
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Nume
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nume complet"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplu.ro"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Telefon
                </Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0722 123 456"
                />
              </div>

              {user?.role === 'master' && (
                <div className="space-y-2">
                  <Label htmlFor="company" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Nume Firmă
                  </Label>
                  <Input
                    id="company"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="SC Exemplu SRL"
                  />
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-violet-600 hover:bg-violet-700"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Se salvează..." : "Salvează Modificările"}
              </Button>
            </form>
          </TabsContent>

          {/* Password Tab */}
          <TabsContent value="password" className="space-y-4 mt-4">
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Parola Curentă</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Introdu parola curentă"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Parola Nouă</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Parola nouă (minim 6 caractere)"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmă Parola Nouă</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repetă parola nouă"
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-violet-600 hover:bg-violet-700"
                disabled={loading}
              >
                <Lock className="w-4 h-4 mr-2" />
                {loading ? "Se schimbă..." : "Schimbă Parola"}
              </Button>
            </form>
          </TabsContent>

          {/* GDPR Tab */}
          <TabsContent value="gdpr" className="space-y-4 mt-4">
            <div className="space-y-4">
              {/* Export Data */}
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <h4 className="font-semibold text-teal-800 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Exportă Datele Mele
                </h4>
                <p className="text-xs text-teal-600 mt-1 mb-3">
                  Descarcă o copie completă a tuturor datelor tale personale (GDPR Art. 20 — Dreptul la portabilitate)
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportData}
                  disabled={exporting}
                  className="border-teal-300 text-teal-700 hover:bg-teal-100"
                  data-testid="export-my-data-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? "Se exportă..." : "Descarcă JSON"}
                </Button>
              </div>

              {/* Privacy Links */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Documente Legale
                </h4>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open("/legal?tab=privacy", "_blank")}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs"
                    data-testid="link-privacy-policy"
                  >
                    Politica de Confidențialitate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open("/legal?tab=terms", "_blank")}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs"
                    data-testid="link-terms"
                  >
                    Termeni și Condiții
                  </Button>
                </div>
              </div>

              {/* Delete Account */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-semibold text-red-800 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Șterge Contul
                </h4>
                <p className="text-xs text-red-600 mt-1 mb-3">
                  Ștergerea contului este permanentă. Datele personale vor fi eliminate, pontajele vor fi anonimizate. (GDPR Art. 17)
                </p>
                {!showDeleteConfirm ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="border-red-300 text-red-700 hover:bg-red-100"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Solicit ștergerea contului
                  </Button>
                ) : (
                  <div className="space-y-3 p-3 bg-red-100 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 text-xs font-medium">
                      <AlertTriangle className="w-4 h-4" />
                      Introduceți parola pentru confirmare:
                    </div>
                    <Input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Parola curentă"
                      className="bg-white"
                      data-testid="delete-account-password"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setShowDeleteConfirm(false); setDeletePassword(""); }}
                      >
                        Anulează
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleDeleteAccount}
                        disabled={deleting || !deletePassword}
                        data-testid="confirm-delete-account-btn"
                      >
                        {deleting ? "Se șterge..." : "Confirmă Ștergerea"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
